/**
 * ExpenseCategoryPicker -- type to search the categories people have already
 * used, or add a new one on the spot.
 *
 * BM-H3620 (5 Oct 2026), Vanessa: "allow people to search for categories but
 * also add categories so we're not trying to guess or add exhaustively at
 * this point". There is no seeded list. The value is just the typed name;
 * the backend creates the category the first time a saved expense uses it
 * (matching ignores case and spacing, so "courier" finds "Courier").
 */
import { useState, useRef, useEffect } from "react";
import { Plus } from "lucide-react";

const FIELD = "w-full bg-transparent border border-navy-border rounded px-3 py-2 text-sm text-white placeholder:text-slate-600";

const norm = (s) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();

export default function ExpenseCategoryPicker({ categories = [], value, onChange }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const q = norm(value);
  const matches = categories.filter((c) => !q || norm(c.name).includes(q));
  const exact = categories.find((c) => norm(c.name) === q);
  const canAdd = q !== "" && !exact;

  const pick = (name) => {
    onChange(name);
    setOpen(false);
  };

  return (
    <div className="relative" ref={wrapRef} data-testid="expense-category-picker">
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        maxLength={60}
        placeholder="Search or add a category"
        className={FIELD}
        role="combobox"
        aria-expanded={open}
        aria-label="Category"
        data-testid="expense-category-input"
      />
      {open && (matches.length > 0 || canAdd) && (
        <ul
          className="absolute z-20 mt-1 w-full max-h-56 overflow-auto rounded border border-navy-border bg-navy-card shadow-lg"
          role="listbox"
          data-testid="expense-category-options"
        >
          {matches.map((c) => (
            <li key={c.id}>
              <button
                type="button"
                role="option"
                aria-selected={exact?.id === c.id}
                onClick={() => pick(c.name)}
                className="w-full text-left px-3 py-2 text-sm text-slate-200 hover:bg-white/5"
                data-testid="expense-category-option"
              >
                {c.name}
              </button>
            </li>
          ))}
          {canAdd && (
            <li>
              <button
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => pick(value.replace(/\s+/g, " ").trim())}
                className="w-full text-left px-3 py-2 text-sm text-teal hover:bg-white/5 inline-flex items-center gap-1.5"
                data-testid="expense-category-add"
              >
                <Plus className="w-3.5 h-3.5" />
                Add &ldquo;{value.replace(/\s+/g, " ").trim()}&rdquo; as a new category
              </button>
            </li>
          )}
        </ul>
      )}
      {canAdd && !open && (
        <p className="mt-1 text-xs text-slate-500" data-testid="expense-category-new-note">
          New category: it will be added when you save.
        </p>
      )}
    </div>
  );
}
